import { useState, useEffect, useRef } from 'react'
import { isInTauriShell } from '@renderer/lib/runtime'

/** True element fullscreen (standard + legacy WebKit), for browsers that omit `fullscreenElement`. */
function getDomFullscreenElement(): Element | null {
  return (
    document.fullscreenElement ??
    (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement ??
    null
  )
}

/**
 * Enter fullscreen with best-effort kiosk hints. `navigationUI: 'hide'` (where supported, e.g. Chromium)
 * reduces browser UI that can appear at screen edges; it does not remove OS gestures (e.g. macOS menu bar reveal).
 */
async function enterDomFullscreen(): Promise<void> {
  const el = document.documentElement

  if (typeof el.requestFullscreen === 'function') {
    try {
      await el.requestFullscreen({ navigationUI: 'hide' })
      return
    } catch {
      try {
        await el.requestFullscreen()
        return
      } catch {
        // Safari / older WebKit may only expose webkit*
      }
    }
  }

  const wk = el as unknown as { webkitRequestFullscreen?: () => void }
  if (typeof wk.webkitRequestFullscreen === 'function') {
    wk.webkitRequestFullscreen()
    return
  }

  throw new Error('Fullscreen API not available')
}

async function exitDomFullscreen(): Promise<void> {
  if (!getDomFullscreenElement()) return

  if (typeof document.exitFullscreen === 'function') {
    await document.exitFullscreen()
    return
  }

  const doc = document as Document & { webkitExitFullscreen?: () => void }
  if (typeof doc.webkitExitFullscreen === 'function') {
    doc.webkitExitFullscreen()
  }
}

// Check if Electron APIs are available
const isElectronAvailable = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof window.api !== 'undefined' &&
    typeof window.api.setFullscreen === 'function'
  )
}

// Check if specific fullscreen APIs exist (for older Electron version compatibility)
const hasToggleFullscreenAPI = (): boolean => {
  return isElectronAvailable() && typeof window.api?.toggleFullscreen === 'function'
}

const hasIsFullscreenAPI = (): boolean => {
  return isElectronAvailable() && typeof window.api?.isFullscreen === 'function'
}

const hasOnFullscreenChangedAPI = (): boolean => {
  return isElectronAvailable() && typeof window.api?.onFullscreenChanged === 'function'
}

export const useFullscreen = (): {
  isFullscreen: boolean
  toggleFullscreen: () => Promise<void>
} => {
  const electronAvailable = isElectronAvailable()
  const [isFullscreen, setIsFullscreen] = useState<boolean>(() => {
    // Initialize state synchronously if possible
    if (electronAvailable && window.api) {
      // For Electron, we'll fetch async, but start with a reasonable default
      // Since app starts in fullscreen, default to true
      return true
    }
    if (isInTauriShell()) {
      // Tauri: default true (kiosk-like); actual state synced in useEffect
      return true
    }
    // For DOM API, check synchronously
    return !!getDomFullscreenElement()
  })
  const unsubscribeRef = useRef<(() => void) | null>(null)

  // Initialize fullscreen state and set up event listeners
  useEffect(() => {
    // Check if ALL required Electron APIs are available
    const canUseElectronAPIs = hasOnFullscreenChangedAPI() && hasIsFullscreenAPI()

    if (canUseElectronAPIs && window.api) {
      // Set up Electron API event listener first
      const unsubscribe = window.api.onFullscreenChanged((state: boolean) => {
        setIsFullscreen(state)
      })
      unsubscribeRef.current = unsubscribe

      // Get initial state from Electron (after listener is set up)
      window.api
        .isFullscreen()
        .then((state) => {
          setIsFullscreen(state)
        })
        .catch((error) => {
          console.error('Error getting initial fullscreen state:', error)
          // Keep default true since app starts in fullscreen
        })

      // Periodic state check as fallback (every 500ms) - only to catch missed events
      // This is a safety net, but the event listener should handle most updates
      const intervalId = setInterval(() => {
        window.api
          ?.isFullscreen()
          .then((state) => {
            setIsFullscreen((prev) => {
              // Only update if state actually changed (avoid unnecessary re-renders)
              return prev !== state ? state : prev
            })
          })
          .catch(() => {
            // Silently ignore errors in periodic check
          })
      }, 500)

      return (): void => {
        clearInterval(intervalId)
        if (unsubscribeRef.current) {
          unsubscribeRef.current()
          unsubscribeRef.current = null
        }
      }
    }
    if (isInTauriShell()) {
      // Tauri: use core window plugin (set_fullscreen, is_fullscreen); no change event, so poll
      const intervalRef = { current: null as ReturnType<typeof setInterval> | null }
      void (async (): Promise<void> => {
        const { getCurrentWindow } = await import('@tauri-apps/api/window')
        const win = getCurrentWindow()
        try {
          const state = await win.isFullscreen()
          setIsFullscreen(state)
        } catch {
          // keep default
        }
        intervalRef.current = setInterval(async () => {
          try {
            const state = await win.isFullscreen()
            setIsFullscreen((prev) => (prev !== state ? state : prev))
          } catch {
            // ignore
          }
        }, 500)
      })()
      return (): void => {
        if (intervalRef.current) clearInterval(intervalRef.current)
      }
    }
    // Fallback to DOM API (for browser or older Electron)
    {
      console.log('[useFullscreen] Using DOM Fullscreen API fallback')
      const handleFullscreenChange = (): void => {
        setIsFullscreen(!!getDomFullscreenElement())
      }

      // Set initial state
      setIsFullscreen(!!getDomFullscreenElement())

      document.addEventListener('fullscreenchange', handleFullscreenChange)
      document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
      return (): void => {
        document.removeEventListener('fullscreenchange', handleFullscreenChange)
        document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
      }
    }
  }, [electronAvailable])

  const toggleFullscreen = async (): Promise<void> => {
    try {
      if (isInTauriShell()) {
        try {
          const { getCurrentWindow } = await import('@tauri-apps/api/window')
          const win = getCurrentWindow()
          const current = await win.isFullscreen()
          await win.setFullscreen(!current)
          const next = await win.isFullscreen()
          setIsFullscreen(next)
        } catch (err) {
          console.warn('Tauri fullscreen toggle failed:', err)
          setIsFullscreen((prev) => !prev)
        }
        return
      }
      // Check if toggleFullscreen API exists (for older Electron version compatibility)
      if (hasToggleFullscreenAPI() && window.api) {
        // Don't use optimistic update - wait for confirmed state to prevent blank screen
        // This solves the race condition where UI updates before Electron completes the toggle

        // Add timeout protection in case IPC call hangs
        const TOGGLE_TIMEOUT_MS = 3000
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Fullscreen toggle timed out')), TOGGLE_TIMEOUT_MS)
        })

        try {
          await Promise.race([window.api.toggleFullscreen(), timeoutPromise])
        } catch (toggleError) {
          console.warn('Fullscreen toggle failed or timed out:', toggleError)
          // On timeout/error, fallback to DOM API
          await toggleFullscreenViaDOM()
          return
        }

        // Force immediate state verification after toggle completes
        // This ensures React state matches actual fullscreen state
        if (hasIsFullscreenAPI()) {
          try {
            const confirmedState = await window.api.isFullscreen()
            setIsFullscreen(confirmedState)
          } catch (verifyError) {
            console.warn('Error verifying fullscreen state after toggle:', verifyError)
            // Fallback: toggle local state based on previous value
            setIsFullscreen((prev) => !prev)
          }

          // Additional safety: trigger a visibility re-check after a short delay
          // This helps recover from any transient blank screen states
          setTimeout(() => {
            window.api?.isFullscreen().then((state) => {
              setIsFullscreen((prev) => (prev !== state ? state : prev))
            }).catch(() => {
              // Silently ignore - best effort recovery
            })
          }, 100)
        } else {
          // No isFullscreen API, toggle local state
          setIsFullscreen((prev) => !prev)
        }
      } else {
        // Fallback to DOM API (older Electron or missing API)
        await toggleFullscreenViaDOM()
      }
    } catch (error) {
      console.error('Error toggling fullscreen:', error)
      // On error, try to recover by fetching actual state or toggling local
      if (hasIsFullscreenAPI() && window.api) {
        try {
          const actualState = await window.api.isFullscreen()
          setIsFullscreen(actualState)
        } catch {
          // If all else fails, toggle local state as fallback
          setIsFullscreen((prev) => !prev)
        }
      } else {
        setIsFullscreen((prev) => !prev)
      }
    }
  }

  // Helper function for DOM fullscreen toggle
  const toggleFullscreenViaDOM = async (): Promise<void> => {
    try {
      if (!getDomFullscreenElement()) {
        await enterDomFullscreen()
      } else {
        await exitDomFullscreen()
      }
      setIsFullscreen(!!getDomFullscreenElement())
    } catch (domError) {
      console.warn('DOM fullscreen toggle failed:', domError)
      setIsFullscreen(!!getDomFullscreenElement())
    }
  }

  return { isFullscreen, toggleFullscreen }
}
